import { toast } from "react-toastify";

export interface ApiErrorMeta {
  error_message: string;
  error_type: string;
  validation_errors?: Record<string, string[]>;
  code: number;
}

export interface ApiErrorResponse {
  success: boolean;
  data: null;
  meta: ApiErrorMeta;
  // Fallback for old error structure
  message?: string;
  errors?: Record<string, string[]>;
}

export interface ValidationError {
  field: string;
  message: string;
}

export class ErrorHandler {
  static handle(error: any): ValidationError[] {
    const validationErrors: ValidationError[] = [];

    // Handle axios error response
    if (error?.response?.data) {
      const errorData: ApiErrorResponse = error.response.data;

      // Check if we have the new error structure with meta
      if (errorData?.meta) {
        const { meta } = errorData;

        // Handle 422 validation errors
        if (meta.code === 422 && meta.validation_errors) {
          Object.entries(meta.validation_errors).forEach(
            ([field, messages]) => {
              if (Array.isArray(messages) && messages.length > 0) {
                validationErrors.push({
                  field,
                  message: messages[0], // Take the first error message for each field
                });
              }
            },
          );

          // Show toast for validation errors using error_message
          if (meta.error_message) {
            toast.error(meta.error_message);
          } else if (validationErrors.length > 0) {
            // Fallback to first validation error
            toast.error(validationErrors[0].message);
          }
        } else if (meta.error_message) {
          // Handle general error messages (401, 403, 500, etc.)
          toast.error(meta.error_message);
        }
      } else {
        // Fallback for old error structure
        if (errorData?.errors) {
          Object.entries(errorData.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              validationErrors.push({
                field,
                message: messages[0],
              });
            }
          });

          if (validationErrors.length > 0) {
            toast.error(validationErrors[0].message);
          }
        } else if (errorData?.message) {
          toast.error(errorData.message);
        }
      }
    } else if (error?.message) {
      // Handle generic error messages
      toast.error(error.message);
    } else {
      // Fallback error message
      toast.error("An unexpected error occurred");
    }

    return validationErrors;
  }

  static getFieldError(field: string, error: any): string | undefined {
    // Handle new error structure
    if (error?.response?.data?.meta?.validation_errors) {
      const messages = error.response.data.meta.validation_errors[field];

      return Array.isArray(messages) && messages.length > 0
        ? messages[0]
        : undefined;
    }

    // Handle old error structure
    if (error?.response?.data?.errors) {
      const messages = error.response.data.errors[field];

      return Array.isArray(messages) && messages.length > 0
        ? messages[0]
        : undefined;
    }

    return undefined;
  }

  static hasFieldError(field: string, error: any): boolean {
    return !!this.getFieldError(field, error);
  }

  /**
   * Get the main error message from the response
   */
  static getErrorMessage(error: any): string {
    // Handle new error structure
    if (error?.response?.data?.meta?.error_message) {
      return error.response.data.meta.error_message;
    }

    // Handle old error structure
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    // Handle generic error
    if (error?.message) {
      return error.message;
    }

    return "An unexpected error occurred";
  }

  /**
   * Get error type from the response
   */
  static getErrorType(error: any): string | undefined {
    return error?.response?.data?.meta?.error_type;
  }

  /**
   * Get error code from the response
   */
  static getErrorCode(error: any): number | undefined {
    return error?.response?.data?.meta?.code;
  }
}
